'use client'

import { EllipsisVerticalIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import React, { useEffect, useState } from 'react'

import SettingAccount from '@/components/dialog/settings/account'
import SettingGeneral from '@/components/dialog/settings/general'
import SettingSecurity from '@/components/dialog/settings/security'
import { CloseIcon, FullscreenIcon } from '@/components/shell-icons'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import ApiKeysPanel from '@/features/settings/api-keys/api-keys-panel'
import BillingPanel from '@/features/settings/billing/billing-panel'
import {
  SettingsNav,
  SettingsPanelTransition,
  SettingsTabBar,
  useTabDirection,
} from '@/features/settings/components/settings-motion'
import DevicesPanel from '@/features/settings/devices/devices-panel'
import {
  buildSettingsPath,
  resolveSettingsEntryReturnTo,
} from '@/features/settings/lib/settings-navigation'
import NotificationsPanel from '@/features/settings/notifications/notifications-panel'
import { useScreenBreakPoint } from '@/hooks/use-screen'
import { SettingTab } from '@/lib/configs/setting-config'

interface SettingDialogProps {
  children?: React.ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
  defaultTab?: SettingTab
  defaultAccountId?: string
  /** Forwarded to Settings → Accounts: spotlight its "Add account" button. */
  highlightAddAccount?: boolean
  highlightAccountId?: string
  manageAccessAccountId?: string
}

export default function SettingDialog({
  children,
  open,
  onOpenChange,
  defaultTab,
  defaultAccountId,
  highlightAddAccount,
  highlightAccountId,
  manageAccessAccountId,
}: SettingDialogProps) {
  const t = useTranslations('common')
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { isMobile } = useScreenBreakPoint()
  const [activeTab, setActiveTab] = useState<SettingTab>(SettingTab.GENERAL)
  const [internalOpen, setInternalOpen] = useState(false)
  const direction = useTabDirection(activeTab)

  const isControlled = open !== undefined
  const isOpen = isControlled ? open : internalOpen
  const returnTo = resolveSettingsEntryReturnTo(pathname, searchParams.toString())
  const expandedSettingsHref = buildSettingsPath(activeTab, returnTo)
  const handleOpenChange = (value: boolean) => {
    if (isControlled) {
      onOpenChange?.(value)
    } else {
      setInternalOpen(value)
    }
  }

  useEffect(() => {
    if (defaultTab) {
      setActiveTab(defaultTab)
    }
  }, [defaultTab])

  // Start warming the exact leaf route as soon as the dialog opens. This gives
  // production prefetching the whole time the user spends in the dialog instead
  // of waiting until they hover the maximize button.
  useEffect(() => {
    if (isOpen) router.prefetch(expandedSettingsHref)
  }, [expandedSettingsHref, isOpen, router])

  // Reown AppKit's wallet picker (<w3m-modal>) is portaled to document.body,
  // outside this dialog's DOM subtree. Without this guard, clicking inside it
  // — e.g. during the Derive "Resume setup" wallet reconnect — registers as
  // an outside interaction and dismisses the dialog mid-flow. Shadow-DOM
  // retargeting means the event target resolves to the <w3m-modal> host
  // element itself, so `closest` is reliable here.
  const handleInteractOutside: NonNullable<
    React.ComponentProps<typeof DialogContent>['onInteractOutside']
  > = (event) => {
    if (event.target instanceof Element && event.target.closest('w3m-modal')) {
      event.preventDefault()
    }
  }

  const openFullPage = () => {
    router.push(expandedSettingsHref)
    handleOpenChange(false)
  }

  const renderPanel = (tab: SettingTab) => {
    switch (tab) {
      case SettingTab.GENERAL:
        return <SettingGeneral onNavigateAway={() => handleOpenChange(false)} />
      case SettingTab.NOTIFICATIONS:
        return <NotificationsPanel />
      case SettingTab.SECURITY:
        return <SettingSecurity />
      case SettingTab.DEVICES:
        return <DevicesPanel />
      case SettingTab.API:
        return <ApiKeysPanel />
      case SettingTab.BILLING:
        return <BillingPanel />
      case SettingTab.ACCOUNT:
        return (
          <SettingAccount
            defaultAccountId={defaultAccountId}
            highlightAddAccount={highlightAddAccount}
            highlightAccountId={highlightAccountId}
            manageAccessAccountId={manageAccessAccountId}
          />
        )
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      {children !== null && (
        <DialogTrigger asChild>
          {children || (
            <Button
              aria-label={t('openSettings')}
              variant="ghost"
              size="icon"
              className="size-7 bg-transparent hover:bg-white/5"
            >
              <EllipsisVerticalIcon className="size-3.5 text-white" />
            </Button>
          )}
        </DialogTrigger>
      )}
      {isMobile ? (
        <DialogContent
          className="top-3 right-3 bottom-3 left-3 h-[calc(100dvh-24px)] max-h-none w-auto max-w-none translate-x-0 translate-y-0 p-0 [&>button]:hidden"
          onInteractOutside={handleInteractOutside}
        >
          <div className="absolute inset-0 flex flex-col">
            <div className="flex shrink-0 items-center justify-between px-5 pt-5 pb-3">
              <DialogTitle className="text-base">{t('settings')}</DialogTitle>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="toolbar"
                  aria-label={t('expand')}
                  onPointerEnter={() => router.prefetch(expandedSettingsHref)}
                  onFocus={() => router.prefetch(expandedSettingsHref)}
                  onClick={openFullPage}
                >
                  <FullscreenIcon />
                </Button>
                <DialogClose asChild>
                  <Button variant="ghost" size="toolbar" aria-label={t('close')}>
                    <CloseIcon className="size-6" />
                  </Button>
                </DialogClose>
              </div>
            </div>
            <SettingsTabBar activeTab={activeTab} onSelect={setActiveTab} className="px-2" />
            <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto p-4">
              <SettingsPanelTransition panelKey={activeTab} direction={direction} axis="x">
                {renderPanel(activeTab)}
              </SettingsPanelTransition>
            </div>
          </div>
        </DialogContent>
      ) : (
        // A fixed height, so switching tabs never resizes the dialog: a short tab leaves room
        // below, a long one scrolls inside the panel. Capped to the viewport on short screens.
        <DialogContent
          className="flex h-[min(37.5rem,calc(100dvh-2rem))] max-w-195 flex-col overflow-hidden p-0 [&>button]:hidden"
          onInteractOutside={handleInteractOutside}
        >
          <div className="flex min-h-0 flex-1">
            <nav className="bg-foreground/5 flex shrink-0 flex-col gap-8 px-2 pt-12 pb-6">
              <div className="px-4">
                <DialogTitle>{t('settings')}</DialogTitle>
              </div>
              <SettingsNav activeTab={activeTab} onSelect={setActiveTab} />
            </nav>
            <div className="flex min-h-0 min-w-0 flex-1 flex-col">
              <div className="border-foreground/10 flex h-12 shrink-0 items-center justify-end gap-1 border-b px-3">
                <Button
                  variant="ghost"
                  size="toolbar"
                  aria-label={t('expand')}
                  title={t('expand')}
                  onPointerEnter={() => router.prefetch(expandedSettingsHref)}
                  onFocus={() => router.prefetch(expandedSettingsHref)}
                  onClick={openFullPage}
                >
                  <FullscreenIcon />
                </Button>
                <DialogClose asChild>
                  <Button variant="ghost" size="toolbar" aria-label={t('close')} title={t('close')}>
                    <CloseIcon className="size-6" />
                  </Button>
                </DialogClose>
              </div>
              <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto p-6">
                <SettingsPanelTransition panelKey={activeTab} direction={direction} axis="y">
                  {renderPanel(activeTab)}
                </SettingsPanelTransition>
              </div>
            </div>
          </div>
        </DialogContent>
      )}
    </Dialog>
  )
}
